import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1150, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', whiteSpace: 'nowrap' as const },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
};

const BAND_COLOR: Record<string, string> = { green: '#188038', yellow: '#e8710a', red: '#d93025', blacklist: '#000' };
const RISK_COLOR: Record<string, string> = { safe: '#188038', warning: '#e8710a', critical: '#d93025' };

// ═══ VAMP 仪表盘 ═══
function VampView() {
  const [vamp, setVamp] = useState<any>(null);

  useEffect(() => { get('/api/v1/vamp').then((r: any) => setVamp(r.data)).catch(() => {}); }, []);

  if (!vamp) return <div style={S.card}>{t('加载中...')}</div>;

  const metric = (label: string, value: React.ReactNode, sub?: string, color?: string) => (
    <div style={S.card}>
      <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: color || '#222' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const ratePct = (vamp.dispute_rate * 100).toFixed(3);

  return (
    <>
      <div style={S.grid}>
        {metric(t('当月争议率'), `${ratePct}%`, `警戒 0.65% / 暂停 1%`, RISK_COLOR[vamp.risk_level])}
        {metric(t('当月订单'), vamp.total_orders)}
        {metric(t('当月争议'), vamp.total_disputes, vamp.margin_to_warn !== null ? `距警戒线还可承受 ${vamp.margin_to_warn} 单` : undefined)}
        {metric(t('胜诉 / 败诉'), `${vamp.disputes_won} / ${vamp.disputes_lost}`, `挽回 $${(vamp.amount_recovered || 0).toFixed(0)}`)}
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('争议率历史（月度）')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('月份')}</th><th style={S.th}>{t('订单')}</th><th style={S.th}>{t('争议')}</th><th style={S.th}>{t('争议率')}</th></tr></thead>
          <tbody>
            {(vamp.history || []).map((h: any) => (
              <tr key={h.month}>
                <td style={S.td}>{h.month}</td><td style={S.td}>{h.total_orders}</td><td style={S.td}>{h.total_disputes}</td>
                <td style={{ ...S.td, color: h.dispute_rate >= 0.0065 ? '#d93025' : '#188038', fontWeight: 600 }}>{(h.dispute_rate * 100).toFixed(3)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 拒付争议 ═══
function DisputesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState('');
  const [doc, setDoc] = useState<string | null>(null);

  const load = () => get('/api/v1/disputes').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    const orderId = prompt('订单 ID：');
    if (!orderId) return;
    const reason = prompt(t('拒付原因码（fraudulent / product_not_received / product_unacceptable / unrecognized / general）：'), 'fraudulent');
    const amount = prompt(t('争议金额 $：'), '0');
    try { await post('/api/v1/disputes', { order_id: orderId, reason_code: reason, amount: Number(amount) }); load(); }
    catch (e: any) { alert(e.message); }
  };

  const build = async (id: string) => {
    setBusy(id);
    try { const r: any = await post(`/api/v1/disputes/${id}/build-evidence`); setDoc(r.data.response_doc); load(); }
    catch (e: any) { alert(e.message); }
    setBusy('');
  };

  const act = async (id: string, path: string, body?: any) => {
    try { await post(`/api/v1/disputes/${id}/${path}`, body); load(); } catch (e: any) { alert(e.message); }
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} onClick={add}>{t('＋ 登记拒付')}</button>
      </div>
      {doc && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong>{t('组卷响应文档')}</strong>
            <button style={S.btn} onClick={() => setDoc(null)}>{t('关闭')}</button>
          </div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 360, overflow: 'auto' }}>{doc}</pre>
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>{t('订单')}</th><th style={S.th}>{t('原因码')}</th><th style={S.th}>{t('金额')}</th>
            <th style={S.th}>{t('证据截止')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('操作')}</th>
          </tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id}>
                <td style={S.td}>{r.order_name || r.order_id}</td>
                <td style={S.td}>{r.reason_code}</td>
                <td style={S.td}>${r.amount}</td>
                <td style={S.td}>{(r.evidence_due_by || '').slice(0, 10) || '-'}</td>
                <td style={S.td}>{r.status}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                  {r.status === 'needs_response' && (
                    <button style={S.btnPrimary} disabled={busy === r.id} onClick={() => build(r.id)}>
                      {busy === r.id ? '组卷中...' : t('🗂 自动组卷')}
                    </button>
                  )}
                  {r.status === 'evidence_ready' && (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button style={S.btn} onClick={() => setDoc(r.response_doc)}>{t('查看文档')}</button>
                      <button style={S.btnPrimary} onClick={() => act(r.id, 'submit')}>{t('标记已提交')}</button>
                    </span>
                  )}
                  {r.status === 'submitted' && (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                      <button style={S.btn} onClick={() => act(r.id, 'resolve', { result: 'won' })}>{t('胜诉')}</button>
                      <button style={S.btn} onClick={() => act(r.id, 'resolve', { result: 'lost' })}>{t('败诉')}</button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={6}>{t('暂无争议记录')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 高风险订单 ═══
function RiskOrdersView() {
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/risk/orders').then((r: any) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/risk/scan-orders'); alert(`扫描 ${r.data.orders_scanned} 单，标记 ${r.data.flagged} 单高风险`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15 }}>{t('高风险订单（预防性干预）')}</h3>
        <button style={S.btnPrimary} disabled={busy} onClick={scan}>{busy ? '扫描中...' : t('🔍 扫描近 7 天订单')}</button>
      </div>
      <table style={S.table}>
        <thead><tr>
          <th style={S.th}>{t('订单')}</th><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('金额')}</th>
          <th style={S.th}>{t('风险分')}</th><th style={S.th}>{t('信号')}</th><th style={S.th}>{t('建议')}</th><th style={S.th}>{t('处理')}</th>
        </tr></thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td style={S.td}>{r.order_name}</td>
              <td style={S.td}>{r.customer_email || '-'}</td>
              <td style={S.td}>${r.total}</td>
              <td style={{ ...S.td, fontWeight: 700, color: r.risk_score >= 70 ? '#d93025' : r.risk_score >= 45 ? '#e8710a' : '#666' }}>{Math.round(r.risk_score)}</td>
              <td style={{ ...S.td, fontSize: 12, maxWidth: 280 }}>{JSON.parse(r.signals_json || '[]').join('；')}</td>
              <td style={S.td}>{r.recommended_action}</td>
              <td style={S.td}>
                <select style={S.input} value={r.status} onChange={async e => { await put(`/api/v1/risk/orders/${r.id}`, { status: e.target.value }); load(); }}>
                  <option value="open">{t('待处理')}</option><option value="verified">{t('已验证')}</option>
                  <option value="reviewed">{t('已审核')}</option><option value="cancelled">{t('已取消订单')}</option>
                </select>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td style={S.td} colSpan={7}>{t('暂无高风险订单')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 售后滥用 ═══
function AbuseView() {
  const [flags, setFlags] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    get('/api/v1/abuse/flags').then((r: any) => setFlags(r.data || [])).catch(() => {});
    get('/api/v1/abuse/customers').then((r: any) => setCustomers(r.data || [])).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const scan = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/abuse/scan'); alert(`分析 ${r.data.customers_analyzed} 客户，新增 ${r.data.new_flags} 个滥用标记`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      <div style={{ ...S.card, display: 'flex', gap: 10 }}>
        <button style={S.btnPrimary} disabled={busy} onClick={scan}>{busy ? '扫描中...' : t('🔄 扫描滥用行为')}</button>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('滥用标记')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('类型')}</th><th style={S.th}>{t('严重度')}</th><th style={S.th}>{t('时间')}</th><th style={S.th}></th></tr></thead>
          <tbody>
            {flags.map((f: any) => (
              <tr key={f.id}>
                <td style={S.td}>{f.customer_email || f.customer_id}</td>
                <td style={S.td}>{f.abuse_type}</td>
                <td style={{ ...S.td, color: f.severity === 'high' ? '#d93025' : '#e8710a' }}>{f.severity}</td>
                <td style={S.td}>{(f.created_at || '').slice(0, 10)}</td>
                <td style={S.td}><button style={S.btn} onClick={async () => { await post(`/api/v1/abuse/flags/${f.id}/clear`); load(); }}>{t('解除')}</button></td>
              </tr>
            ))}
            {flags.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无滥用标记')}</td></tr>}
          </tbody>
        </table>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('客户风险分层')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('客户')}</th><th style={S.th}>{t('风险分')}</th><th style={S.th}>{t('等级')}</th><th style={S.th}>{t('因素')}</th><th style={S.th}>{t('调整')}</th></tr></thead>
          <tbody>
            {customers.map((cu: any) => (
              <tr key={cu.customer_id}>
                <td style={S.td}>{cu.customer_email || cu.customer_id}</td>
                <td style={S.td}>{Math.round(cu.risk_score)}</td>
                <td style={{ ...S.td, color: BAND_COLOR[cu.risk_band], fontWeight: 700 }}>{cu.risk_band}</td>
                <td style={{ ...S.td, fontSize: 12 }}>{JSON.parse(cu.factors_json || '[]').join('，')}</td>
                <td style={S.td}>
                  <select style={S.input} value={cu.risk_band} onChange={async e => { await put(`/api/v1/abuse/customers/${encodeURIComponent(cu.customer_id)}/band`, { band: e.target.value }); load(); }}>
                    <option value="green">green</option><option value="yellow">yellow</option>
                    <option value="red">red</option><option value="blacklist">blacklist</option>
                  </select>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无评级数据 — 点击"扫描滥用行为"')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('vamp');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>FraudSentinel</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('拒付防御与售后风控 — VAMP 监控 · 自动组卷 · 滥用侦测 · 风险分层')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'vamp')} onClick={() => setTab('vamp')}>{t('VAMP 合规')}</div>
        <div style={S.tab(tab === 'disputes')} onClick={() => setTab('disputes')}>{t('拒付争议')}</div>
        <div style={S.tab(tab === 'risk')} onClick={() => setTab('risk')}>{t('风险订单')}</div>
        <div style={S.tab(tab === 'abuse')} onClick={() => setTab('abuse')}>{t('售后滥用')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'vamp' && <VampView />}
      {tab === 'disputes' && <DisputesView />}
      {tab === 'risk' && <RiskOrdersView />}
      {tab === 'abuse' && <AbuseView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
